import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Heart,
  Trash2,
  Loader2,
  Image,
  FileText,
  Award,
  TrendingUp,
  Clock,
  Users,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LikesManagement = ({ language = 'fr' }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentLikes, setRecentLikes] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [deleting, setDeleting] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsResponse = await axios.get(`${API_URL}/likes/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(statsResponse.data);
      
      // Fetch recent likes
      const params = filterType !== 'all' ? `?target_type=${filterType}` : '';
      const likesResponse = await axios.get(`${API_URL}/likes/recent${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentLikes(likesResponse.data);
    } catch (error) {
      console.error('Error fetching likes data:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  }, [token, filterType, language]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (likeId) => {
    if (!window.confirm(language === 'fr' ? 'Supprimer ce like ?' : 'Delete this like?')) {
      return;
    }
    
    setDeleting(likeId);
    try {
      await axios.delete(`${API_URL}/likes/${likeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'fr' ? 'Like supprimé' : 'Like deleted');
      await fetchData();
    } catch (error) {
      console.error('Error deleting like:', error);
      toast.error(language === 'fr' ? 'Erreur de suppression' : 'Delete error');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'gallery_photo':
        return <Image className="w-4 h-4 text-purple-500" />;
      case 'blog_article':
        return <FileText className="w-4 h-4 text-blue-500" />;
      case 'agent_honor':
        return <Award className="w-4 h-4 text-yellow-500" />;
      default:
        return <Heart className="w-4 h-4 text-red-500" />;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      gallery_photo: language === 'fr' ? 'Photo' : 'Photo',
      blog_article: language === 'fr' ? 'Article' : 'Article',
      agent_honor: language === 'fr' ? 'Agent du mois' : 'Agent of the month'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Heart className="w-6 h-6 text-red-500 fill-red-500" />
            {language === 'fr' ? 'Gestion des Likes' : 'Likes Management'}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {language === 'fr' ? 'Voir et gérer les interactions des visiteurs' : 'View and manage visitor interactions'}
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-likes-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          {language === 'fr' ? 'Actualiser' : 'Refresh'}
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-red-100 bg-red-50/50">
            <CardContent className="p-4 text-center">
              <Heart className="w-8 h-8 mx-auto mb-2 text-red-500 fill-red-500" />
              <p className="text-3xl font-bold text-gray-900">{stats.total_likes}</p>
              <p className="text-sm text-gray-500">{language === 'fr' ? 'Total likes' : 'Total likes'}</p>
            </CardContent>
          </Card>
          <Card className="border-blue-100 bg-blue-50/50">
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 mx-auto mb-2 text-blue-500" />
              <p className="text-3xl font-bold text-gray-900">{stats.recent_24h}</p>
              <p className="text-sm text-gray-500">{language === 'fr' ? 'Dernières 24h' : 'Last 24h'}</p>
            </CardContent>
          </Card>
          <Card className="border-purple-100 bg-purple-50/50">
            <CardContent className="p-4 text-center">
              <Image className="w-8 h-8 mx-auto mb-2 text-purple-500" />
              <p className="text-3xl font-bold text-gray-900">{stats.by_type?.gallery_photo || 0}</p>
              <p className="text-sm text-gray-500">{language === 'fr' ? 'Photos' : 'Photos'}</p>
            </CardContent>
          </Card>
          <Card className="border-green-100 bg-green-50/50">
            <CardContent className="p-4 text-center">
              <FileText className="w-8 h-8 mx-auto mb-2 text-green-500" />
              <p className="text-3xl font-bold text-gray-900">{stats.by_type?.blog_article || 0}</p>
              <p className="text-sm text-gray-500">{language === 'fr' ? 'Articles' : 'Articles'}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Liked */}
      {stats?.top_liked?.length > 0 && (
        <Card className="border-yellow-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-yellow-600" />
              {language === 'fr' ? 'Publications les plus likées' : 'Most liked publications'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.top_liked.map((item, index) => (
                <div 
                  key={`${item.target_type}-${item.target_id}`}
                  className="flex items-center gap-3 p-2 rounded-lg bg-gray-50"
                >
                  <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-700 font-bold text-sm">
                    {index + 1}
                  </div>
                  {getTypeIcon(item.target_type)}
                  <span className="flex-1 text-sm font-medium truncate">{item.title}</span>
                  <Badge className="bg-red-100 text-red-700">
                    <Heart className="w-3 h-3 mr-1 fill-current" />
                    {item.likes_count}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Likes */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-5 h-5 text-gray-600" />
              {language === 'fr' ? 'Likes récents' : 'Recent likes'}
            </CardTitle>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-40" data-testid="filter-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                <SelectItem value="gallery_photo">{language === 'fr' ? 'Photos' : 'Photos'}</SelectItem>
                <SelectItem value="blog_article">{language === 'fr' ? 'Articles' : 'Articles'}</SelectItem>
                <SelectItem value="agent_honor">{language === 'fr' ? 'Agent du mois' : 'Agent'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {recentLikes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Heart className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>{language === 'fr' ? 'Aucun like trouvé' : 'No likes found'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentLikes.map((like) => (
                <div 
                  key={like.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50"
                  data-testid={`like-row-${like.id}`}
                >
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    {like.liker_name === 'Anonyme' ? (
                      <Heart className="w-5 h-5 text-red-500" />
                    ) : (
                      <span className="font-semibold text-red-600">
                        {like.liker_name.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">{like.liker_name}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      {getTypeIcon(like.target_type)}
                      <span className="truncate">{like.target_title || getTypeLabel(like.target_type)}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400">{formatDate(like.created_at)}</p>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(like.id)}
                      disabled={deleting === like.id}
                      className="text-red-500 hover:bg-red-50 h-7 w-7 mt-1"
                      data-testid={`delete-like-${like.id}`}
                    >
                      {deleting === like.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LikesManagement;
